<template>
  <div class="home">
    <h2>TechSupport</h2>
    <b-button @click="raisePopup({ target: $event.target })" variant="primary">
      Add new TechSupport
    </b-button>
    <!-- Filtering -->
    <b-row>
      <b-col md="12" class="my-1">
        <b-form-group label-cols-sm="1" label="Filter" class="mb-0">
          <b-input-group>
            <b-form-input
              v-model="filter.id"
              placeholder="by id"
              @keypress.enter="forceFilter"
            />

            <b-form-input
              v-model="filter.whitelabelId"
              placeholder="by whitelabelId"
              @keypress.enter="forceFilter"
            />

            <b-form-input
              v-model="filter.url"
              placeholder="by url"
              @keypress.enter="forceFilter"
            />

            <b-input-group-append>
              <b-button
                :disabled="!filter.id && !filter.whitelabelId && !filter.url"
                @click="
                  filter = {};
                  forceFilter();
                "
                >Clear</b-button
              >
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Info modal -->
    <b-modal
      id="TechSupportModal"
      v-bind:title="this.isEdit ? 'Edit TechSupport' : 'New TechSupport'"
      @ok="TechSupportFormSubmit"
    >
      <b-form>
        <b-form-group
          id="newwhitelabelIdInputGroup1"
          label="whitelabelId:"
          label-for="newwhitelabelIdInputGroup1"
        >
          <b-form-input
            id="newwhitelabelIdInput1"
            type="text"
            v-model="form.whitelabelId"
            placeholder="Enter whitelabelId"
          />
        </b-form-group>

        <b-form-group
          id="newurlInputGroup1"
          label="url:"
          label-for="newurlInputGroup1"
        >
          <b-form-input
            id="newurlInput1"
            type="text"
            v-model="form.url"
            placeholder="Enter url"
          />
        </b-form-group>
      </b-form>
    </b-modal>

    <b-container fluid>
      <b-row>
        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="2" label="Sort" class="mb-0">
            <b-input-group>
              <b-form-select v-model="sortBy" :options="sortOptions">
                <option slot="first" :value="null">-- none --</option>
              </b-form-select>
              <b-form-select
                :disabled="!sortBy"
                v-model="sortDesc"
                slot="append"
              >
                <option :value="false">Asc</option>
                <option :value="true">Desc</option>
              </b-form-select>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="3" label="Per page" class="mb-0">
            <b-form-select :options="pageOptions" v-model="perPage" />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Main table element -->
      <b-table
        show-empty
        small
        hover
        stacked="md"
        :items="items"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :filter="filter"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @filtered="onFiltered"
        ref="TechSupportTable"
        class="TechSupportTable"
      >
        <template v-slot:cell(whitelabelId)="row">
          {{ row.item.whitelabelId }} ({{ row.item['whitelabel.name'] }})
        </template>
        <template v-slot:cell(actions)="row">
          <b-button
            size="sm"
            class="m-1"
            @click="row.toggleDetails"
            variant="info"
          >
            …
          </b-button>
          <b-button
            size="sm"
            class="m-1"
            variant="warning"
            @click="
              raisePopup({
                target: $event.target,
                isEdit: true,
                TechSupport: row.item
              })
            "
          >
            ✎
          </b-button>
          <b-button
            size="sm"
            variant="danger"
            class="m-1 ml-3"
            @click="
              deleteTechSupport({
                target: $event.target,
                TechSupport: row.item
              })
            "
          >
            X
          </b-button>
        </template>

        <template v-slot:row-details="row">
          <b-card>
            <ul>
              <li v-for="(value, key) in row.item" :key="key">
                {{ key }}: {{ value }}
              </li>
            </ul>
          </b-card>
        </template>
      </b-table>

      <b-row>
        <b-col md="6" class="my-1">
          <b-pagination
            :total-rows="totalRows"
            :per-page="perPage"
            v-model="currentPage"
            class="my-0"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
/* eslint-disable no-magic-numbers */

const originalForm = {
  id: null,
  whitelabelId: null,
  url: null
};

export default {
  data() {
    return {
      currentPage: 1,
      perPage: 20,
      pageOptions: [5, 20, 50, 100, 500],
      sortBy: null,
      sortDesc: false,
      filter: {
        whitelabelId: '',
        url: ''
      },
      form: {
        ...originalForm
      },
      isEdit: false
    };
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => {
          return { text: f.label, value: f.key };
        });
    },
    fields() {
      return [
        {
          key: 'id',
          label: 'id',
          sortable: true,
          sortDirection: 'desc'
        },

        {
          key: 'whitelabelId',
          label: 'whitelabelId',
          sortable: true,
          class: 'text-center'
        },

        {
          key: 'url',
          label: 'url',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'createdAt',
          label: 'Created',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'updatedAt',
          label: 'Updated',
          sortable: true,
          class: 'text-center'
        },
        { key: 'actions', label: 'Actions' }
      ];
    },
    totalRows() {
      return this.$store.state.techSupport.count;
    }
  },
  methods: {
    forceFilter() {
      this.$refs.TechSupportTable.refresh();
    },
    raisePopup({ target, isEdit = false, TechSupport = null }) {
      this.isEdit = isEdit;
      if (TechSupport && isEdit) {
        this.form = Object.keys(originalForm).reduce(
          (acc, current) =>
            TechSupport[current]
              ? {
                  ...acc,
                  [current]: String(TechSupport[current])
                }
              : acc,
          {}
        );
      } else {
        this.TechSupportFormReset();
      }
      this.$root.$emit('bv::show::modal', 'TechSupportModal', target);
    },
    deleteTechSupport({ TechSupport = null }) {
      if (!TechSupport) {
        return;
      }
      const promise = this.$store.dispatch('removeTechSupport', TechSupport.id);

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        this.$refs.TechSupportTable.refresh();
      });
    },
    onFiltered() {
      // Trigger pagination to update the number of buttons/pages due to filtering
      // this.currentPage = 1;
    },
    items(ctx) {
      const currentFilter = Object.keys(this.filter).reduce(
        (acc, curr) =>
          this.filter[curr] ? { ...acc, [curr]: this.filter[curr] } : acc,
        {}
      );

      const promise = this.$store.dispatch('getTechSupports', {
        ctx,
        filter: currentFilter
      });

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        return this.$store.state.techSupport.rows || [];
      });
    },
    TechSupportFormReset() {
      this.form = { ...originalForm };
    },
    async TechSupportFormSubmit(event) {
      if (!this.isEdit) {
        await this.$store.dispatch('addTechSupport', {
          payload: this.form,
          event
        });
      } else {
        await this.$store.dispatch('updateTechSupport', {
          payload: this.form,
          event
        });
      }

      this.TechSupportFormReset();
      this.$refs.TechSupportTable.refresh();
    }
  },
  mounted() {
    this.$store.dispatch('getVersions');
  }
};
</script>
<style scoped type="text/scss">
.block {
  border-bottom: #dadada 1px solid;
  padding-bottom: 1em;
  margin-bottom: 3em;
}
.container {
  max-width: none;
}
.TechSupportTable {
  font-size: 0.8rem;
}
.btn {
  font-size: 0.8rem;
}

td[data-label='Actions'] {
  white-space: nowrap;
}
</style>
